import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Snackbar,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Switch,
  FormControlLabel,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { supabase } from '../../services/supabase';
import type { League, Club, Player } from '../../types/models';
import { apiCache } from '../../services/apiCache';
import { useAdminRole } from '../../contexts/AdminRoleContext';
import { fixTypes } from '../../services/adminPermissions';

// Cache keys and TTL values
const LEAGUES_CACHE_KEY = 'admin_leagues_list';
const CLUBS_CACHE_KEY = 'admin_clubs_list';
const JUDGES_CACHE_KEY = 'admin_judges_list';
const CACHE_TTL = 2 * 60 * 1000; // 2 minutes

const LeaguesAdmin: React.FC = () => {
  const { userRole, isSuperAdmin, loading: roleLoading } = useAdminRole();
  const [leagues, setLeagues] = useState<League[]>([]);
  const [clubs, setClubs] = useState<Club[]>([]);
  const [judges, setJudges] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [leagueName, setLeagueName] = useState('');
  const [selectedClubId, setSelectedClubId] = useState<string>('');
  const [selectedJudgeId, setSelectedJudgeId] = useState<string>('');
  const [isHidden, setIsHidden] = useState(false);
  const [isTournament, setIsTournament] = useState(false);
  const [isFinishedLeague, setIsFinishedLeague] = useState(false);
  const [editingLeagueId, setEditingLeagueId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error'
  });

  // Fetch data when role information is available
  useEffect(() => {
    if (!roleLoading) {
      // Load all data in parallel
      Promise.all([
        fetchClubs(),
        fetchJudges()
      ]).then(() => {
        fetchLeagues();
      });
    }
  }, [roleLoading, userRole?.id]);

  const getCacheKey = (baseKey: string) => {
    // Create user-specific cache keys
    return userRole 
      ? `${baseKey}_${userRole.id}_${userRole.role}`
      : baseKey;
  };

  const clearCaches = () => {
    // Clear all related caches when data changes
    apiCache.remove(getCacheKey(LEAGUES_CACHE_KEY));
    apiCache.remove(getCacheKey(CLUBS_CACHE_KEY));
    apiCache.remove(getCacheKey(JUDGES_CACHE_KEY));
  };

  const fetchLeagues = async () => {
    setLoading(true);
    try {
      // Get from cache or fetch from API
      const leaguesData = await apiCache.getOrFetch<League[]>(
        getCacheKey(LEAGUES_CACHE_KEY),
        async () => {
          
          let queryResult;
          
          if (isSuperAdmin) {
            // Superadmins see all leagues
            queryResult = await supabase
              .from('leagues')
              .select(`
                *,
                clubs (
                  id,
                  name
                ),
                judge:judge_id (
                  id,
                  nickname,
                  full_name
                )
              `)
              .order('name', { ascending: true });
          } else if (userRole?.club_id) {
            // Regular admins only see leagues from their club
            queryResult = await supabase
              .from('leagues')
              .select(`
                *,
                clubs (
                  id,
                  name
                ),
                judge:judge_id (
                  id,
                  nickname,
                  full_name
                )
              `)
              .eq('club_id', userRole.club_id)
              .order('name', { ascending: true });
          } else {
            // No club assigned, return empty result
            return [];
          }
      
          const { data, error } = queryResult;
          
          if (error) {
            throw error;
          }
      
          // Fix types with the helper function
          const typedData = fixTypes<League>(data || []);
          
          // Handle default values for judge_id if needed
          return typedData.map(league => ({
            ...league,
            judge_id: league.judge_id || '' // Set default value for judge_id
          }));
        },
        CACHE_TTL
      );
      
      setLeagues(leaguesData);
    } catch (error: any) {
      console.error('Error fetching leagues:', error.message);
      showSnackbar('Failed to load leagues', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchClubs = async () => {
    try {
      // Get from cache or fetch from API
      const clubsData = await apiCache.getOrFetch<Club[]>(
        getCacheKey(CLUBS_CACHE_KEY),
        async () => {
          
          let queryResult;
          
          if (isSuperAdmin) {
            // Superadmins can see all clubs
            queryResult = await supabase
              .from('clubs')
              .select('*')
              .order('name', { ascending: true });
          } else if (userRole?.club_id) {
            // Regular admins can only see their assigned club
            queryResult = await supabase
              .from('clubs')
              .select('*')
              .eq('id', userRole.club_id)
              .order('name', { ascending: true });
          } else {
            // User doesn't have club access
            return [];
          }
          
          const { data, error } = queryResult;
          
          if (error) {
            throw error;
          }
          
          // Fix types to ensure proper typing
          return fixTypes<Club>(data || []);
        },
        CACHE_TTL
      );
      
      setClubs(clubsData);
      
      // Set default club when clubs are loaded
      if (clubsData.length > 0 && !selectedClubId) {
        setSelectedClubId(clubsData[0].id);
      }
    } catch (error: any) {
      console.error('Error fetching clubs:', error.message);
      showSnackbar('Failed to load clubs', 'error');
    }
  };
  
  const fetchJudges = async () => {
    try {
      // Get from cache or fetch from API
      const judgesData = await apiCache.getOrFetch<Player[]>(
        getCacheKey(JUDGES_CACHE_KEY),
        async () => {
          // Players don't have club_id, so no need for admin filtering
          const { data, error } = await supabase
            .from('players')
            .select('*')
            .eq('is_judge', true)
            .order('nickname', { ascending: true });
    
          if (error) {
            throw error;
          }
          
          // Use fixTypes to ensure proper typing
          return fixTypes<Player>(data || []);
        },
        CACHE_TTL
      );
      
      setJudges(judgesData);
      
      // Set default judge when judges are loaded
      if (judgesData.length > 0 && !selectedJudgeId) {
        setSelectedJudgeId(judgesData[0].id);
      }
    } catch (error: any) {
      console.error('Error fetching judges:', error.message);
      showSnackbar('Failed to load judges', 'error');
    }
  };

  const handleOpenDialog = (league?: League) => {
    if (league) {
      setEditingLeagueId(league.id);
      setLeagueName(league.name);
      setSelectedClubId(league.club_id);
      setSelectedJudgeId(league.judge_id);
      setIsHidden(league.hidden || false);
      setIsTournament(league.is_tournament || false);
      setIsFinishedLeague(league.is_finished_league || false);
    } else {
      setEditingLeagueId(null);
      setLeagueName('');
      setSelectedClubId(clubs.length > 0 ? clubs[0].id : '');
      setSelectedJudgeId(judges.length > 0 ? judges[0].id : '');
      setIsHidden(false);
      setIsTournament(false);
      setIsFinishedLeague(false);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setLeagueName('');
    setSelectedClubId('');
    setSelectedJudgeId('');
    setIsHidden(false);
    setIsTournament(false);
    setIsFinishedLeague(false);
    setEditingLeagueId(null);
  };

  const showSnackbar = (message: string, severity: 'success' | 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleClubChange = (event: SelectChangeEvent) => {
    setSelectedClubId(event.target.value);
  };
  
  const handleJudgeChange = (event: SelectChangeEvent) => {
    setSelectedJudgeId(event.target.value);
  };

  const handleSubmit = async () => {
    if (!leagueName.trim()) {
      showSnackbar('League name is required', 'error');
      return;
    }

    if (!selectedClubId) {
      showSnackbar('Please select a club', 'error');
      return;
    }
    
    if (!selectedJudgeId) {
      showSnackbar('Please select a judge', 'error');
      return;
    }

    try {
      if (editingLeagueId) {
        // Update existing league
        const { error } = await supabase
          .from('leagues')
          .update({
            name: leagueName,
            club_id: selectedClubId,
            judge_id: selectedJudgeId,
            hidden: isHidden,
            is_tournament: isTournament,
            is_finished_league: isFinishedLeague
          })
          .eq('id', editingLeagueId);

        if (error) throw error;
        showSnackbar('League updated successfully', 'success');
      } else {
        // Create new league
        const { error } = await supabase
          .from('leagues')
          .insert([{
            name: leagueName,
            club_id: selectedClubId,
            judge_id: selectedJudgeId,
            hidden: isHidden,
            is_tournament: isTournament,
            is_finished_league: isFinishedLeague
          }]);

        if (error) throw error;
        showSnackbar('League created successfully', 'success');
      }

      // Clear all caches to ensure fresh data
      clearCaches();
      
      handleCloseDialog();
      fetchLeagues();
    } catch (error: any) {
      console.error('Error saving league:', error.message);
      showSnackbar(`Failed to ${editingLeagueId ? 'update' : 'create'} league`, 'error');
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this league? This will also delete all associated games.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('leagues')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Clear all caches to ensure fresh data
      clearCaches();
      
      showSnackbar('League deleted successfully', 'success');
      fetchLeagues();
    } catch (error: any) {
      console.error('Error deleting league:', error.message);
      showSnackbar('Failed to delete league', 'error');
    }
  };

  // Show loading if we're waiting for role data or initial data fetch
  if (roleLoading || (loading && leagues.length === 0)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6" component="h2">
          Manage Leagues
        </Typography>
        <Button 
          variant="contained" 
          color="secondary" 
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{ borderRadius: 2 }}
          disabled={clubs.length === 0}
        >
          Add League
        </Button>
      </Box>

      {clubs.length === 0 && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 2, bgcolor: 'rgba(255, 152, 0, 0.1)' }}>
          <Typography color="warning.main">
            You need to create at least one club before you can add leagues.
          </Typography>
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : leagues.length === 0 ? (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
          <Typography>No leagues found. Create your first league!</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.paper' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>League Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Club</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Judge</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {leagues.map((league) => {
                // Handle the joined data from Supabase
                const clubName = league.clubs ? (league.clubs as any).name : 'Unknown Club';
                const judgeName = league.judge ? (league.judge as any).nickname : 'Unknown Judge';
                
                return (
                  <TableRow key={league.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell component="th" scope="row">
                      {league.name}
                    </TableCell>
                    <TableCell>
                      {clubName}
                    </TableCell>
                    <TableCell>
                      {judgeName}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {league.is_tournament && (
                          <Chip
                            icon={<EmojiEventsIcon />}
                            label="Tournament"
                            size="small"
                            color="primary"
                            variant="filled"
                          />
                        )}
                        {league.is_finished_league && (
                          <Chip
                            icon={<CheckCircleIcon />}
                            label="Finished"
                            size="small"
                            color="success"
                            variant="filled"
                          />
                        )}
                        {league.hidden ? (
                          <Chip
                            icon={<VisibilityOffIcon />}
                            label="Hidden"
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        ) : (
                          <Chip
                            label="Visible"
                            size="small"
                            color="success"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton onClick={() => handleOpenDialog(league)} color="primary" size="small">
                        <EditIcon />
                      </IconButton>
                      <IconButton onClick={() => handleDelete(league.id)} color="error" size="small">
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Dialog for adding/editing league */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingLeagueId ? 'Edit League' : 'Add New League'}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="name"
            label="League Name"
            type="text"
            fullWidth
            variant="outlined"
            value={leagueName}
            onChange={(e) => setLeagueName(e.target.value)}
            sx={{ mt: 1, mb: 2 }}
          />
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel id="club-select-label">Club</InputLabel>
            <Select
              labelId="club-select-label"
              id="club-select"
              value={selectedClubId}
              onChange={handleClubChange}
              label="Club"
            >
              {clubs.map((club) => (
                <MenuItem key={club.id} value={club.id}>
                  {club.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          
          <FormControl fullWidth variant="outlined" sx={{ mb: 2 }}>
            <InputLabel id="judge-select-label">Judge</InputLabel>
            <Select
              labelId="judge-select-label"
              id="judge-select"
              value={selectedJudgeId}
              onChange={handleJudgeChange}
              label="Judge"
            >
              {judges.map((judge) => (
                <MenuItem key={judge.id} value={judge.id}>
                  {judge.nickname} ({judge.full_name})
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControlLabel
            control={
              <Switch
                checked={isHidden}
                onChange={(e) => setIsHidden(e.target.checked)}
                color="warning"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <VisibilityOffIcon sx={{ fontSize: 20, color: isHidden ? 'warning.main' : 'text.secondary' }} />
                <Typography>
                  Hide league results (show "In Progress" instead)
                </Typography>
              </Box>
            }
            sx={{ mb: 1 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={isTournament}
                onChange={(e) => setIsTournament(e.target.checked)}
                color="primary"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <EmojiEventsIcon sx={{ fontSize: 20, color: isTournament ? 'primary.main' : 'text.secondary' }} />
                <Typography>
                  Tournament mode (show podium and special stats)
                </Typography>
              </Box>
            }
            sx={{ mb: 1 }}
          />

          <FormControlLabel
            control={
              <Switch
                checked={isFinishedLeague}
                onChange={(e) => setIsFinishedLeague(e.target.checked)}
                color="success"
              />
            }
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 20, color: isFinishedLeague ? 'success.main' : 'text.secondary' }} />
                <Typography>
                  Finished league (show results with points graph)
                </Typography>
              </Box>
            }
          />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleCloseDialog} color="primary" sx={{ borderRadius: 2 }}>
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit} 
            variant="contained" 
            color="secondary"
            sx={{ borderRadius: 2 }}
          >
            {editingLeagueId ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={5000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity} 
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeaguesAdmin;